// Every typeface MDS offers. All come from the Google Fonts catalog under an
// open license and ship self-hosted through Fontsource, so a site built on
// MDS makes no request to a font CDN.

export type FontOption = {
  id: string
  name: string
  stack: string
  designer: string
  package: string
  specimen: string
  note: string
  /** The open license it ships under; SIL Open Font License unless stated. */
  license?: string
  /**
   * Display size relative to Instrument Serif, so a headline keeps its width
   * when the typeface changes. Measured from "Design for America." at 100px.
   */
  scale?: number
}

export const displayFonts: FontOption[] = [
  {
    id: 'instrument-serif',
    name: 'Instrument Serif',
    stack: '"Instrument Serif", "Iowan Old Style", Georgia, serif',
    designer: 'Rodrigo Fuenzalida and Jordan Egstad',
    package: '@fontsource/instrument-serif',
    specimen: 'https://fonts.google.com/specimen/Instrument+Serif',
    note: 'Default. A condensed display serif in the tradition of the National Design Studio sites.',
  },
  {
    id: 'newsreader',
    scale: 0.78,
    name: 'Newsreader',
    stack: '"Newsreader Variable", Georgia, serif',
    designer: 'Production Type',
    package: '@fontsource-variable/newsreader',
    specimen: 'https://fonts.google.com/specimen/Newsreader',
    note: 'An editorial serif with optical sizes, comfortable from headline to paragraph.',
  },
  {
    id: 'fraunces',
    scale: 0.7,
    name: 'Fraunces',
    stack: '"Fraunces Variable", Georgia, serif',
    designer: 'Undercase Type',
    package: '@fontsource-variable/fraunces',
    specimen: 'https://fonts.google.com/specimen/Fraunces',
    note: 'A soft, characterful Old Style serif for warmer, more expressive missions.',
  },
  {
    id: 'source-serif',
    scale: 0.68,
    name: 'Source Serif 4',
    stack: '"Source Serif 4 Variable", Georgia, serif',
    designer: 'Frank Grießhammer for Adobe',
    package: '@fontsource-variable/source-serif-4',
    specimen: 'https://fonts.google.com/specimen/Source+Serif+4',
    note: 'A sturdy transitional serif with an institutional voice.',
  },
  {
    id: 'inter',
    scale: 0.69,
    name: 'Inter Display',
    stack: '"Inter Variable", Inter, system-ui, sans-serif',
    designer: 'Rasmus Andersson',
    package: '@fontsource-variable/inter',
    specimen: 'https://fonts.google.com/specimen/Inter',
    note: 'A tight sans headline for technical and operational missions.',
  },
  {
    id: 'roboto-slab',
    scale: 0.69,
    name: 'Roboto Slab',
    stack: '"Roboto Slab Variable", Rockwell, Georgia, serif',
    designer: 'Christian Robertson',
    package: '@fontsource-variable/roboto-slab',
    specimen: 'https://fonts.google.com/specimen/Roboto+Slab',
    note: 'A sturdy slab serif with the plain authority of civic signage.',
    license: 'Apache License 2.0',
  },
]

export const sansFonts: FontOption[] = [
  {
    id: 'inter',
    name: 'Inter',
    stack: '"Inter Variable", Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    designer: 'Rasmus Andersson',
    package: '@fontsource-variable/inter',
    specimen: 'https://fonts.google.com/specimen/Inter',
    note: 'Default. A neutral grotesque with tabular figures for data-dense screens.',
  },
  {
    id: 'geist',
    name: 'Geist',
    stack: '"Geist Variable", system-ui, sans-serif',
    designer: 'Vercel with Basement Studio',
    package: '@fontsource-variable/geist',
    specimen: 'https://fonts.google.com/specimen/Geist',
    note: 'A precise, technical sans common across recent federal launches.',
  },
  {
    id: 'public-sans',
    name: 'Public Sans',
    stack: '"Public Sans Variable", system-ui, sans-serif',
    designer: 'USWDS',
    package: '@fontsource-variable/public-sans',
    specimen: 'https://fonts.google.com/specimen/Public+Sans',
    note: 'The USWDS default, for teams migrating gradually.',
  },
  {
    id: 'figtree',
    name: 'Figtree',
    stack: '"Figtree Variable", system-ui, sans-serif',
    designer: 'Erik Kennedy',
    package: '@fontsource-variable/figtree',
    specimen: 'https://fonts.google.com/specimen/Figtree',
    note: 'A friendly geometric sans for public-facing benefit services.',
  },
  {
    id: 'atkinson',
    name: 'Atkinson Hyperlegible',
    stack: '"Atkinson Hyperlegible Next Variable", system-ui, sans-serif',
    designer: 'Braille Institute with Applied Design Works',
    package: '@fontsource-variable/atkinson-hyperlegible-next',
    specimen: 'https://fonts.google.com/specimen/Atkinson+Hyperlegible+Next',
    note: 'Drawn for low-vision readers: every letter is distinct from the ones it could be mistaken for.',
  },
  {
    id: 'plex',
    name: 'IBM Plex Sans',
    stack: '"IBM Plex Sans Variable", system-ui, sans-serif',
    designer: 'Mike Abbink with Bold Monday',
    package: '@fontsource-variable/ibm-plex-sans',
    specimen: 'https://fonts.google.com/specimen/IBM+Plex+Sans',
    note: 'An engineered humanist sans with a distinct, machined character for tools and data.',
  },
]

export const monoFont: FontOption = {
  id: 'geist-mono',
  name: 'Geist Mono',
  stack: '"Geist Mono Variable", ui-monospace, SFMono-Regular, Menlo, monospace',
  designer: 'Vercel with Basement Studio',
  package: '@fontsource-variable/geist-mono',
  specimen: 'https://fonts.google.com/specimen/Geist+Mono',
  note: 'Code, token names and technical labels.',
}

/** Attribute rules that switch the font tokens without a re-render. */
export function fontsCss(banner: string) {
  const rules = [
    ...displayFonts.map(
      (font) =>
        `[data-font-display="${font.id}"] {\n  --mds-font-display: ${font.stack};\n  --mds-font-display-scale: ${font.scale ?? 1};\n}\n`,
    ),
    ...sansFonts.map(
      (font) => `[data-font-sans="${font.id}"] {\n  --mds-font-sans: ${font.stack};\n}\n`,
    ),
  ]
  return [`/* ${banner} */`, ...rules].join('\n')
}
